import { memo, useMemo } from 'react';
import { ArtistActionsWrapper } from '../Actions/ArtistActions';
import { usePlayback } from '../../contexts/PlaybackContext';
import { useComposerIdSet } from '../../utils/useComposerIdSet';
import { ComposerAwareLink } from '../shared/ComposerAwareLink';
import {
  deriveQueuePointer,
  extractQueueItemMetadata,
} from '../../lib/playback/queueDisplay';

const AlbumSongDetails = memo(() => {
  const { playerState, queueSession, queueItems } = usePlayback();
  const currentSong = playerState?.track_window?.current_track ?? null;
  const composerIds = useComposerIdSet();

  const pointer = useMemo(() => deriveQueuePointer(queueSession, queueItems), [queueItems, queueSession]);
  const currentQueueItem = pointer.item;
  const queueMetadata = useMemo(() => extractQueueItemMetadata(currentQueueItem), [currentQueueItem]);

  const displayTitle = queueMetadata.title ?? currentSong?.name ?? '—';
  const displayRecordingLabel = queueMetadata.recordingName ?? queueMetadata.albumName ?? null;
  const queueArtistsText = queueMetadata.artists;

  if (!currentSong && !currentQueueItem) return <></>;

  const renderArtists = () => {
    if (currentSong) {
      const artists = currentSong.artists.slice(0, 3);
      return artists.map((artist, index) => (
        <span key={artist.uri}>
          <ArtistActionsWrapper artist={artist} trigger={['contextMenu']}>
            <ComposerAwareLink target='_blank' artist={artist} composerIds={composerIds}>
              {artist.name}
            </ComposerAwareLink>
          </ArtistActionsWrapper>
          {index < artists.length - 1 && ', '}
        </span>
      ));
    }
    return queueArtistsText ?? null;
  };

  const albumArtUrl =
    currentSong?.album?.images && currentSong.album.images.length > 0
      ? currentSong.album.images[0]?.url ?? null
      : null;

  return (
    <div className='flex flex-row items-center'>
      {albumArtUrl ? (
        <img alt='Album Cover' className='album-cover' src={albumArtUrl} />
      ) : null}
      <div id='song-and-artist-name'>
        <p className='text-white font-bold song-title' title={displayTitle}>
          {displayTitle}
        </p>
        <p className='text-gray-200 song-artist' title={queueArtistsText ?? undefined}>
          {renderArtists()}
        </p>
        {displayRecordingLabel ? (
          <p className='text-gray-400 queue-recording-label' title={displayRecordingLabel}>
            {displayRecordingLabel}
          </p>
        ) : null}
      </div>
    </div>
  );
});

AlbumSongDetails.displayName = 'FullScreenAlbumSongDetails';

export default AlbumSongDetails;
